import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { eventReminderService } from '../../../services/eventReminderService';

const EventRemindersPanel = () => {
  const [reminders, setReminders] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sending, setSending] = useState(null);

  const [formData, setFormData] = useState({
    eventId: '',
    reminderName: '',
    reminderType: 'both',
    recipientType: 'students',
    sendBeforeHours: 24,
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [remindersData, eventsData] = await Promise.all([
        eventReminderService?.getAllReminders(),
        eventReminderService?.getEvents()
      ]);
      setReminders(remindersData || []);
      setEvents(eventsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      const selectedEvent = events?.find(ev => ev?.id === formData?.eventId);
      if (!selectedEvent) return;

      const eventDate = new Date(selectedEvent?.scheduled_at);
      const scheduledAt = new Date(
        eventDate?.getTime() - formData?.sendBeforeHours * 60 * 60 * 1000
      );

      await eventReminderService?.createReminder({
        ...formData,
        scheduledAt: scheduledAt?.toISOString()
      });

      setShowCreateForm(false);
      setFormData({
        eventId: '',
        reminderName: '',
        reminderType: 'both',
        recipientType: 'students',
        sendBeforeHours: 24,
        subject: '',
        message: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating reminder:', error);
      alert('Failed to create reminder. Please try again.');
    }
  };

  const handleSendNow = async (reminderId) => {
    if (!confirm('Are you sure you want to send this reminder now?')) return;

    try {
      setSending(reminderId);
      const result = await eventReminderService?.sendReminder(reminderId);
      alert(
        `Reminder sent successfully!\n\nTotal Recipients: ${result?.totalRecipients}\nSent: ${result?.sentCount}\nFailed: ${result?.failedCount}`
      );
      loadData();
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder. Please check the console for details.');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (reminderId) => {
    if (!confirm('Are you sure you want to delete this reminder?')) return;

    try {
      await eventReminderService?.deleteReminder(reminderId);
      loadData();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete reminder.');
    }
  };

  const reminderTypeOptions = [
    { value: 'email', label: 'Email Only' },
    { value: 'sms', label: 'SMS Only' },
    { value: 'both', label: 'Email & SMS' }
  ];

  const recipientTypeOptions = [
    { value: 'students', label: 'Students Only' },
    { value: 'institutes', label: 'Institutes Only' },
    { value: 'both', label: 'Students & Institutes' }
  ];

  const eventOptions = events?.map(event => ({
    value: event?.id,
    label: `${event?.title} - ${new Date(event?.scheduled_at)?.toLocaleDateString()}`
  }));

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-100 text-blue-800', label: 'Scheduled' },
      sent: { color: 'bg-green-100 text-green-800', label: 'Sent' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig?.[status] || statusConfig?.scheduled;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground">
                Event Reminders
              </h3>
              <p className="text-sm text-muted-foreground">
                Schedule and manage email/SMS reminders for upcoming events
              </p>
            </div>
          </div>
          <Button
            iconName="Plus"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            Create Reminder
          </Button>
        </div>

        {showCreateForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
            <h4 className="text-lg font-semibold text-foreground mb-4">New Reminder</h4>
            <div className="space-y-4">
              <Select
                label="Select Event"
                options={eventOptions}
                value={formData?.eventId}
                onChange={(value) => handleChange('eventId', value)}
                required
              />

              <Input
                label="Reminder Name"
                type="text"
                value={formData?.reminderName}
                onChange={(e) => handleChange('reminderName', e?.target?.value)}
                placeholder="e.g., 24 Hour Event Reminder"
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Delivery Method"
                  options={reminderTypeOptions}
                  value={formData?.reminderType}
                  onChange={(value) => handleChange('reminderType', value)}
                  required
                />

                <Select
                  label="Recipients"
                  options={recipientTypeOptions}
                  value={formData?.recipientType}
                  onChange={(value) => handleChange('recipientType', value)}
                  required
                />
              </div>

              <Input
                label="Send Before Event (hours)"
                type="number"
                value={formData?.sendBeforeHours}
                onChange={(e) => handleChange('sendBeforeHours', parseInt(e?.target?.value))}
                min="1"
                required
              />

              {(formData?.reminderType === 'email' || formData?.reminderType === 'both') && (
                <Input
                  label="Email Subject"
                  type="text"
                  value={formData?.subject}
                  onChange={(e) => handleChange('subject', e?.target?.value)}
                  placeholder="Event Reminder"
                  required
                />
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message *
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => handleChange('message', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  rows="4"
                  placeholder="Enter your reminder message..."
                  required
                />
                {formData?.reminderType === 'sms' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    SMS messages are limited to 160 characters
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button type="submit" iconName="Save">
                  Create Reminder
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {reminders?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Bell" size={48} className="mx-auto mb-4" color="var(--color-muted-foreground)" />
              <p className="text-muted-foreground">No reminders scheduled yet</p>
              <p className="text-sm text-muted-foreground mt-2">
                Create your first reminder to notify students and institutes about upcoming events
              </p>
            </div>
          ) : (
            reminders?.map((reminder) => (
              <div
                key={reminder?.id}
                className="p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-foreground">
                        {reminder?.reminder_name}
                      </h4>
                      {getStatusBadge(reminder?.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {reminder?.event?.title}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        Scheduled: {new Date(reminder?.scheduled_at)?.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Mail" size={14} />
                        {reminder?.reminder_type === 'both' ? 'Email & SMS' : 
                         reminder?.reminder_type === 'email' ? 'Email' : 'SMS'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Users" size={14} />
                        {reminder?.recipient_type === 'both' ? 'All' : 
                         reminder?.recipient_type === 'students' ? 'Students' : 'Institutes'}
                      </span>
                    </div>
                    {reminder?.status === 'sent' && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        Sent to {reminder?.sent_count} of {reminder?.recipients_count} recipients
                        {reminder?.failed_count > 0 && (
                          <span className="text-red-600 ml-2">
                            ({reminder?.failed_count} failed)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {reminder?.status === 'scheduled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        iconName="Send"
                        onClick={() => handleSendNow(reminder?.id)}
                        disabled={sending === reminder?.id}
                      >
                        {sending === reminder?.id ? 'Sending...' : 'Send Now'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="Trash2"
                      onClick={() => handleDelete(reminder?.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EventRemindersPanel;
